import React from "react";
import dayjs from "dayjs";
import styles from "./Order.module.scss";
import contactLensIcon from "images/contact-lens.svg";
import eyeglassesIcon from "images/eyeglasses.svg";
import onlineOrderIcon from "images/online-order.svg";
import truck from "images/truck.svg";
import smallLocationPin from "images/small-location-pin.svg";
import { Order as OrderType } from "types/order";

const SHOW_TRACKING_LINKS = false;

export const Order: React.FC<{ order: OrderType; statusPage?: boolean }> = ({
  order,
  statusPage,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const formattedDate = (date: string) => dayjs(date).format("MM/DD/YY");
  const hasItemizedRows =
    order.onlineOrder && order.isExpandable && (order.items?.length || 0) > 1;
  const alwaysShowItemized =
    !!statusPage && order.onlineOrder && (order.items?.length || 0) > 0;
  const showExpandedItems = alwaysShowItemized || expanded;
  const formatEye = (value?: string) => {
    if (!value) return "";

    const normalized = value.trim().toUpperCase();
    if (normalized === "OD") return "Right (OD)";
    if (normalized === "OS") return "Left (OS)";

    return value;
  };
  const renderTrackingLink = (url?: string, label = "Track package") => {
    if (!SHOW_TRACKING_LINKS || !url) return null;

    return (
      <a
        href={url}
        className={styles.itemTrackingLink}
        target="_blank"
        rel="noreferrer"
      >
        {label}
      </a>
    );
  };
  let orderIcon = "";
  let orderTypeName = "";
  if (order.orderTypeName === "soft_contacts" || order.orderTypeName === "S") {
    orderIcon = contactLensIcon;
    orderTypeName = "Contact Lenses";
  } else if (
    order.orderTypeName === "eyeglasses" || 
    order.orderTypeName === "E"
  ) {
    orderIcon = eyeglassesIcon;
    orderTypeName = "Eyeglasses";
  } else {
    orderIcon = onlineOrderIcon;
    orderTypeName = "Dry Eye";
  }
  orderIcon = order.onlineOrder ? onlineOrderIcon : orderIcon;
  orderTypeName = order.onlineOrder ? "Online Order" : orderTypeName;

  const renderItemizedList = () => {
    if (!hasItemizedRows && !alwaysShowItemized) return null;

    return (
      <div className={styles.itemizedSection}>
        {hasItemizedRows && !alwaysShowItemized ? (
          <button
            type="button"
            className={styles.expandButton}
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded ? "Hide order items" : "Show order items"}
          </button>
        ) : null}
        {showExpandedItems ? (
          <div className={styles.itemizedPanel}>
            {renderTrackingLink(order.sharedTrackingUrl)}
            <div className={styles.itemizedHeader}>
              <span>Item</span>
              <span>Eye</span>
              <span>Status</span>
            </div>
            <ul className={styles.itemizedList}>
              {order.items?.map((item, index) => (
                <li
                  className={styles.itemizedRow}
                  key={`${order.orderNum}-${item.productName}-${item.statusCode}-${index}`}
                >
                  <span>{item.productName || "-"}</span>
                  <span className={styles.eyeCell}>
                    <span>{formatEye(item.odOs)}</span>
                    {renderTrackingLink(item.trackingUrl, "Track")}
                  </span>
                  <span>{item.displayStatus || item.statusCode || "-"}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.order}>
      <div className={styles.thumbnail}>
        <img
          className={styles.thumbnailIcon}
          src={orderIcon}
          alt="" /* NB: alt left blank, redundant with below copy */
        />
        <div className={styles.thumbnailDescription}>{orderTypeName}</div>
      </div>
      <div className={styles.desktopOnly}>
        <div className={styles.data}>
          <div className={styles.item}>
            <div className={styles.prop}>Order Date</div>
            <div className={styles.value}>{formattedDate(order.orderDate)}</div>
          </div>
          <div className={styles.item}>
            <div className={styles.prop}>Order No.</div>
            <div className={styles.value}>{`#${order.orderNum}`}</div>
          </div>
          <div className={styles.largeItem}>
            <div className={styles.prop}>Status</div>
            <div className={styles.value}>
              {order.displayStatus}
              <div className={styles.byline}>{order.displayByline}</div>
              {order.expectDate && order.displayStatus === "In the works" && (
                <div className={styles.promisedDate}>
                  Est. Arrival: {formattedDate(order.expectDate)}
                </div>
              )}
            </div>
          </div>
          {order.fulfillmentDestination &&
            order.fulfillmentType === "shipping" && (
              <div className={styles.destinationBar}>
                <img src={truck} className={styles.logo} alt="truck" />
                Shipped to{" "}
                <span className={styles.destination}>
                  {order.fulfillmentDestination}
                </span>
              </div>
            )}
          {order.fulfillmentDestination && order.fulfillmentType === "pickup" && (
            <div className={styles.destinationBar}>
              <img
                src={smallLocationPin}
                className={styles.logo}
                alt="small location pin"
                />
              Pickup at{" "}
              <span className={styles.destination}>
                <a href={order.fulfillmentDestinationMapUrl}>
                  {order.fulfillmentDestination}
                </a>
              </span>
            </div>
          )}
          {renderItemizedList()}
        </div>
      </div>

      {/* Mobile layout */}
      <div className={styles.mobileOnly}>
        <div className={styles.item}>
          <div className={styles.prop}>Order Date</div>
          <div className={styles.value}>{formattedDate(order.orderDate)}</div>
        </div>
        <div className={styles.item}>
          <div className={styles.prop}>Order No.</div>
          <div className={styles.value}>{`#${order.orderNum}`}</div>
        </div>
        <div className={styles.data}>
          <div className={styles.largeItem}>
            <div className={styles.prop}>Status</div>
            <div className={styles.value}>
              {order.displayStatus}
              <div className={styles.byline}>{order.displayByline}</div>
              {order.expectDate && order.displayStatus === "In the works" && (
                <div className={styles.promisedDate}>
                  Est. Arrival: {formattedDate(order.expectDate)}
                </div>
              )}
            </div>
          </div>
          {order.fulfillmentDestination && order.fulfillmentType === "shipping" && (
            <div className={styles.destinationBar}>
              <img src={truck} className={styles.logo} alt="truck" />
              Shipped to{" "}
              <span className={styles.destination}>
                {order.fulfillmentDestination}
              </span>
            </div>
          )}
          {order.fulfillmentDestination && order.fulfillmentType === "pickup" && (
            <div className={styles.destinationBar}>
              <img src={smallLocationPin} className={styles.logo} alt="small location pin" />
              Pickup at{" "}
              <span className={styles.destination}>
                <a href={order.fulfillmentDestinationMapUrl}>
                  {order.fulfillmentDestination}
                </a>
              </span>
            </div>
          )}
          {renderItemizedList()}
        </div>
      </div>
    </div>
  );
};
